import { Bike, Check, Zap, ShoppingBag } from "lucide-react";
import { Eyebrow, Reveal } from "./motion";

const IMG_PREMIUM = "/premium-illustration.webp";

const IMG_FOLDED = "/kiloan-illustration.png";

function Card({ className = "", children, testId }) {
    return (
        <div
            data-testid={testId}
            className={`group overflow-hidden rounded-3xl border border-border bg-white shadow-brand transition-all duration-300 hover:-translate-y-1.5 hover:shadow-brand-lg ${className}`}
        >
            {children}
        </div>
    );
}

export default function Services() {
    return (
        <section id="layanan" className="relative py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-6">
                <Reveal>
                    <Eyebrow>Layanan Kami</Eyebrow>
                    <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-ink sm:text-5xl">
                        Setiap pakaian layak mendapatkan perawatan terbaik.
                    </h2>
                </Reveal>

                <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12">
                    {/* Satuan Premium — hero card */}
                    <Reveal className="lg:col-span-6" delay={0}>
                        <Card testId="service-card-premium" className="h-full">
                            <div className="relative h-60 overflow-hidden sm:h-72">
                                <img
                                    src={IMG_PREMIUM}
                                    alt="Ilustrasi layanan dry cleaning premium DigiKlin"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                                <span className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand backdrop-blur">
                                    Premium Care
                                </span>
                            </div>
                            <div className="p-8 sm:p-10">
                                <h3 className="font-display text-2xl font-bold text-ink sm:text-3xl">
                                    Laundry Satuan Premium
                                </h3>
                                <p className="mt-3 max-w-lg leading-relaxed text-ink/60">
                                    Perawatan satu per satu untuk pakaian
                                    terbaik Anda — kemeja kerja, gaun, jas,
                                    hingga kebaya. Disetrika rapi presisi dan
                                    dikemas tersegel individual.
                                </p>
                                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                                    {[
                                        "Perawatan khusus per jenis kain",
                                        "Setrika rapi presisi",
                                        "Dikemas tersegel individual",
                                    ].map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-2.5 text-sm font-medium text-ink/70"
                                        >
                                            <Check
                                                size={16}
                                                className="mt-0.5 shrink-0 text-brand"
                                                strokeWidth={3}
                                            />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-7 font-display text-lg font-bold text-brand">
                                    Mulai Rp 15.000<span className="text-sm font-semibold text-ink/40">/pcs</span>
                                </p>
                            </div>
                        </Card>
                    </Reveal>

                    {/* Kiloan */}
                    <Reveal className="lg:col-span-6" delay={0.1}>
                        <Card testId="service-card-kiloan" className="flex h-full flex-col">
                            <div className="relative h-60 overflow-hidden sm:h-72">
                                <img
                                    src={IMG_FOLDED}
                                    alt="Ilustrasi layanan laundry kiloan DigiKlin"
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                                <span className="absolute left-5 top-5 rounded-full bg-white/90 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-brand backdrop-blur">
                                    Kiloan Hemat
                                </span>
                            </div>
                            <div className="flex flex-1 flex-col p-8 sm:p-10">
                                <h3 className="font-display text-2xl font-bold text-ink sm:text-3xl">
                                    Laundry Kiloan
                                </h3>
                                <p className="mt-3 leading-relaxed text-ink/60">
                                    Perawatan praktis dan menyeluruh untuk
                                    pakaian sehari-hari Anda. Dicuci dengan
                                    proses yang higienis, dikeringkan dengan
                                    optimal, disetrika rapi, dan dikemas
                                    bersih agar siap digunakan kembali.
                                </p>
                                <ul className="mt-6 space-y-3">
                                    {[
                                        "Proses cuci bersih & higienis",
                                        "Setrika rapi dan nyaman dipakai",
                                        "Dikemas bersih & praktis",
                                    ].map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-2.5 text-sm font-medium text-ink/70"
                                        >
                                            <Check
                                                size={16}
                                                className="mt-0.5 shrink-0 text-brand"
                                                strokeWidth={3}
                                            />
                                            {f}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-auto pt-7 font-display text-lg font-bold text-brand">
                                    Mulai Rp 6.500<span className="text-sm font-semibold text-ink/40">/kg</span>
                                </p>
                            </div>
                        </Card>
                    </Reveal>

                    {/* Small perks */}
                    <Reveal className="lg:col-span-4" delay={0}>
                        <Card testId="service-card-antar-jemput" className="h-full">
                            <div className="p-8 sm:p-10">
                                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-mist text-brand">
                                    <Bike size={22} />
                                </span>
                                <h3 className="mt-5 font-display text-xl font-bold text-ink">
                                    Antar Jemput Gratis
                                </h3>
                                <p className="mt-2.5 text-sm leading-relaxed text-ink/60">
                                    Jadwalkan penjemputan, kurir DigiKlin
                                    datang ke lokasi Anda tanpa biaya tambahan.
                                </p>
                            </div>
                        </Card>
                    </Reveal>

                    <Reveal className="lg:col-span-4" delay={0.08}>
                        <Card testId="service-card-express" className="h-full">
                            <div className="p-8 sm:p-10">
                                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-mist text-brand">
                                    <Zap size={22} />
                                </span>
                                <h3 className="mt-5 font-display text-xl font-bold text-ink">
                                    Express 6 Jam
                                </h3>
                                <p className="mt-2.5 text-sm leading-relaxed text-ink/60">
                                    Butuh cepat? Layanan prioritas selesai
                                    hanya dalam 6 jam — tanpa antrean panjang.
                                </p>
                            </div>
                        </Card>
                    </Reveal>

                    <Reveal className="lg:col-span-4" delay={0.16}>
                        <Card testId="service-card-sepatu" className="h-full">
                            <div className="p-8 sm:p-10">
                                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-mist text-brand">
                                    <ShoppingBag size={22} />
                                </span>
                                <h3 className="mt-5 font-display text-xl font-bold text-ink">
                                    Sepatu & Tas
                                </h3>
                                <p className="mt-2.5 text-sm leading-relaxed text-ink/60">
                                    Perawatan khusus sepatu dan tas agar
                                    kembali bersih seperti baru.
                                </p>
                            </div>
                        </Card>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
